import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { deleteDialog } from 'src/app/shared/dialog';
import {
  AGENT_PERMISSIONS,
  CAPABILITIES,
  CAPABILITY_LABELS,
  Capability,
  CapabilityModel,
  CapabilityModelOptions,
  CapabilityModelRequest,
  Provider,
} from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { AiAgentsApiService } from '../../services/ai-agents-api.service';
import { blankToNull } from '../../utils/clipboard';

/** The typed helper fields above the raw options JSON. */
export interface OptionHelpers {
  voices: string[];
  defaultVoice: string;
  format: string;
  dimensions: number | null;
}

export interface CapabilityGroup {
  capability: Capability;
  label: string;
  models: CapabilityModel[];
  /** The default model of this capability, or null when none is set. */
  defaultModel: CapabilityModel | null;
}

/** Options a new model of the capability starts with (the seeded OpenAI shape). */
export function starterOptions(capability: Capability): CapabilityModelOptions {
  if (capability === 'SPEECH') return { voices: ['alloy', 'nova', 'shimmer'], defaultVoice: 'nova', format: 'mp3' };
  if (capability === 'EMBEDDING') return { dimensions: 1536 };
  return {};
}

/** Parses the raw options text: it must be a JSON object ('' = {}). */
export function parseOptionsJson(text: string): { value: CapabilityModelOptions | null; error: string } {
  const t = (text || '').trim();
  if (!t) return { value: {}, error: '' };
  let parsed: unknown;
  try {
    parsed = JSON.parse(t);
  } catch (e) {
    return { value: null, error: 'Not valid JSON: ' + ((e as Error)?.message || 'parse error') };
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { value: null, error: 'Options must be a JSON object, e.g. {"dimensions": 1536}.' };
  }
  return { value: parsed as CapabilityModelOptions, error: '' };
}

export function formatOptions(options: CapabilityModelOptions | null | undefined): string {
  return JSON.stringify(options || {}, null, 2);
}

/** Reads the helper fields out of an options object. */
export function helpersFromOptions(options: CapabilityModelOptions | null | undefined): OptionHelpers {
  const o = options || {};
  const voices = Array.isArray(o.voices) ? o.voices.filter((v) => typeof v === 'string' && v.trim() !== '').map((v) => v.trim()) : [];
  const dims = typeof o.dimensions === 'number' ? o.dimensions : o.dimensions !== undefined && o.dimensions !== null ? Number(o.dimensions) : null;
  return {
    voices,
    defaultVoice: typeof o.defaultVoice === 'string' ? o.defaultVoice : '',
    format: typeof o.format === 'string' ? o.format : '',
    dimensions: dims !== null && Number.isFinite(dims) ? dims : null,
  };
}

/**
 * Writes the helper fields of the capability into the options object; other keys stay as they are.
 * SPEECH: voices (de-duplicated), defaultVoice (cleared when not in voices), format. EMBEDDING: dimensions.
 */
export function applyHelpers(capability: Capability, options: CapabilityModelOptions | null | undefined, h: OptionHelpers): CapabilityModelOptions {
  const out: CapabilityModelOptions = { ...(options || {}) };
  if (capability === 'SPEECH') {
    const voices = Array.from(new Set((h.voices || []).map((v) => String(v).trim()).filter((v) => v !== '')));
    out.voices = voices;
    if (h.defaultVoice && voices.includes(h.defaultVoice)) out.defaultVoice = h.defaultVoice;
    else delete out.defaultVoice;
    if (h.format && h.format.trim()) out.format = h.format.trim();
    else delete out.format;
  } else if (capability === 'EMBEDDING') {
    if (h.dimensions !== null && h.dimensions !== undefined && String(h.dimensions) !== '') out.dimensions = Number(h.dimensions);
    else delete out.dimensions;
  }
  return out;
}

/** Problems of the options for the capability; '' = fine. */
export function optionsProblem(capability: Capability, options: CapabilityModelOptions): string {
  if (capability === 'SPEECH') {
    const voices = options.voices;
    if (!Array.isArray(voices) || voices.length === 0 || voices.some((v) => typeof v !== 'string' || !v.trim())) {
      return 'A speech model needs at least one voice (options.voices).';
    }
    const bad = voices.find((v) => !VOICE_NAME.test(v));
    if (bad !== undefined) {
      return 'Voice "' + bad + '": a voice name is 1-40 letters, digits, _ or -.';
    }
    if (options.defaultVoice !== undefined && !voices.includes(options.defaultVoice)) {
      return 'The default voice must be one of the voices.';
    }
    if (options.format !== undefined && !SPEECH_FORMATS.includes(String(options.format))) {
      return 'The audio format must be one of ' + SPEECH_FORMATS.join(', ') + '.';
    }
  }
  if (capability === 'EMBEDDING' && options.dimensions !== undefined) {
    const d = options.dimensions;
    if (typeof d !== 'number' || !Number.isInteger(d) || d < 1 || d > 8192) return 'Dimensions must be a whole number from 1 to 8192.';
  }
  if (capability === 'TRANSCRIPTION' && options['responseFormat'] !== undefined && !TRANSCRIPTION_FORMATS.includes(String(options['responseFormat']))) {
    return 'responseFormat must be one of ' + TRANSCRIPTION_FORMATS.join(', ') + '.';
  }
  return '';
}

/** The same rules as the service (CapabilityService.options). */
export const SPEECH_FORMATS = ['mp3', 'opus', 'aac', 'wav'];
export const TRANSCRIPTION_FORMATS = ['json', 'verbose_json', 'text'];
const VOICE_NAME = /^[A-Za-z0-9_-]{1,40}$/;

/** The models grouped by capability in the fixed order, each with its default. Empty groups are kept (to show "none"). */
export function groupCapabilityModels(models: CapabilityModel[], filter: Capability | '' = ''): CapabilityGroup[] {
  return CAPABILITIES.filter((c) => !filter || c === filter).map((capability) => {
    const list = models
      .filter((m) => m.capability === capability)
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || (a.displayName || '').localeCompare(b.displayName || ''));
    return { capability, label: CAPABILITY_LABELS[capability], models: list, defaultModel: list.find((m) => m.isDefault) || null };
  });
}

/** After a model became the default: the others of its capability are no longer default (as the service does). */
export function applyDefault(models: CapabilityModel[], saved: CapabilityModel): CapabilityModel[] {
  const known = models.some((m) => m.id === saved.id);
  const list = known ? models.map((m) => (m.id === saved.id ? saved : m)) : [...models, saved];
  if (!saved.isDefault) return list;
  return list.map((m) => (m.id !== saved.id && m.capability === saved.capability && m.isDefault ? { ...m, isDefault: false } : m));
}

interface CapabilityForm {
  capability: Capability;
  providerId: string;
  modelId: string;
  displayName: string;
  enabled: boolean;
  isDefault: boolean;
  optionsText: string;
}

@Component({
  selector: 'app-ai-capability-models-page',
  templateUrl: './capability-models-page.component.html',
  styleUrls: ['../../ai-agents.shared.css'],
})
export class CapabilityModelsPageComponent implements OnInit {
  readonly P = AGENT_PERMISSIONS;
  readonly capabilities = CAPABILITIES;
  readonly capabilityLabels = CAPABILITY_LABELS;
  readonly formatOptions = formatOptions;

  providers: Provider[] = [];
  models: CapabilityModel[] = [];
  groups: CapabilityGroup[] = [];
  capabilityFilter: Capability | '' = '';
  loading = false;
  loadError = '';

  editorOpen = false;
  editingId: string | null = null;
  form: CapabilityForm = this.blankForm('TRANSCRIPTION');
  helpers: OptionHelpers = helpersFromOptions({});
  jsonError = '';
  formError = '';
  saving = false;

  constructor(
    private api: AiAgentsApiService,
    public perms: AgentPermissionsService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const c = this.route.snapshot.queryParamMap.get('capability') as Capability | null;
    this.capabilityFilter = c && CAPABILITIES.includes(c) ? c : '';
    if (this.perms.has(this.P.providerList)) {
      this.api.listProviders().subscribe({ next: (list) => (this.providers = list || []) });
    }
    this.load();
  }

  readonly speechFormats = SPEECH_FORMATS;

  providerName(m: CapabilityModel): string {
    const p = this.providers.find((x) => x.id === m.providerId);
    return p ? p.displayName : m.providerDisplayName || m.providerId;
  }

  onFilterChange(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { capability: this.capabilityFilter || null },
      replaceUrl: true,
    });
    this.regroup();
  }

  load(): void {
    this.loading = true;
    this.loadError = '';
    this.api.listCapabilityModels().subscribe({
      next: (list) => {
        this.models = list || [];
        this.regroup();
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message || 'Loading the capability models failed.';
      },
    });
  }

  optionsSummary(m: CapabilityModel): string {
    const o = m.options || {};
    if (m.capability === 'SPEECH') {
      const voices = Array.isArray(o.voices) ? o.voices : [];
      return `${voices.length} voices${o.defaultVoice ? ', default ' + o.defaultVoice : ''}${o.format ? ', ' + o.format : ''}`;
    }
    if (m.capability === 'EMBEDDING') return o.dimensions ? `${o.dimensions} dimensions` : '-';
    const keys = Object.keys(o);
    return keys.length ? keys.join(', ') : '-';
  }

  // ------------------------------------------------------------ editor

  openCreate(capability?: Capability): void {
    const cap = capability || this.capabilityFilter || 'TRANSCRIPTION';
    this.editingId = null;
    this.form = this.blankForm(cap);
    this.form.providerId = this.providers[0]?.id || '';
    // The first model of a capability becomes its default.
    this.form.isDefault = !this.models.some((m) => m.capability === cap && m.isDefault);
    this.helpers = helpersFromOptions(parseOptionsJson(this.form.optionsText).value);
    this.jsonError = '';
    this.formError = '';
    this.editorOpen = true;
  }

  openEdit(m: CapabilityModel): void {
    this.editingId = m.id;
    this.form = {
      capability: m.capability,
      providerId: m.providerId,
      modelId: m.modelId,
      displayName: m.displayName,
      enabled: m.enabled,
      isDefault: m.isDefault,
      optionsText: formatOptions(m.options),
    };
    this.helpers = helpersFromOptions(m.options);
    this.jsonError = '';
    this.formError = '';
    this.editorOpen = true;
  }

  closeEditor(): void {
    this.editorOpen = false;
  }

  /** A new model that changes capability starts from that capability's options. */
  onCapabilityChange(capability: Capability): void {
    this.form.capability = capability;
    if (!this.editingId) {
      this.form.optionsText = formatOptions(starterOptions(capability));
      this.form.isDefault = !this.models.some((m) => m.capability === capability && m.isDefault);
      this.helpers = helpersFromOptions(starterOptions(capability));
      this.jsonError = '';
    }
  }

  /** Raw JSON edited: the helpers follow when it is a valid object. */
  onOptionsTextChange(text: string): void {
    this.form.optionsText = text;
    const parsed = parseOptionsJson(text);
    this.jsonError = parsed.error;
    if (parsed.value) this.helpers = helpersFromOptions(parsed.value);
  }

  /** A helper edited: the raw JSON follows (only while it is valid, so a typo is never overwritten). */
  onHelpersChange(): void {
    const parsed = parseOptionsJson(this.form.optionsText);
    if (!parsed.value) return;
    const next = applyHelpers(this.form.capability, parsed.value, this.helpers);
    if (this.helpers.defaultVoice && !(next.voices || []).includes(this.helpers.defaultVoice)) this.helpers.defaultVoice = '';
    this.form.optionsText = formatOptions(next);
    this.jsonError = '';
  }

  setVoices(voices: string[] | null): void {
    // The chips field also accepts "a, b, c" in one go.
    const list = (voices || []).flatMap((v) => String(v).split(',')).map((v) => v.trim()).filter((v) => v !== '');
    this.helpers = { ...this.helpers, voices: Array.from(new Set(list)) };
    this.onHelpersChange();
  }

  get currentDefault(): CapabilityModel | null {
    return this.models.find((m) => m.capability === this.form.capability && m.isDefault && m.id !== this.editingId) || null;
  }

  save(): void {
    this.formError = '';
    const parsed = parseOptionsJson(this.form.optionsText);
    if (!parsed.value) {
      this.jsonError = parsed.error;
      this.formError = 'Fix the options JSON first.';
      return;
    }
    const body: CapabilityModelRequest = {
      capability: this.form.capability,
      providerId: blankToNull(this.form.providerId),
      modelId: blankToNull(this.form.modelId),
      displayName: blankToNull(this.form.displayName),
      options: parsed.value,
      isDefault: this.form.isDefault,
      enabled: this.form.enabled,
    };
    if (!body.providerId || !body.modelId || !body.displayName) {
      this.formError = 'Provider, model ID and display name are required.';
      return;
    }
    const problem = optionsProblem(this.form.capability, parsed.value);
    if (problem) {
      this.formError = problem;
      return;
    }
    if (this.form.isDefault && !this.form.enabled) {
      this.formError = 'The default model must be enabled.';
      return;
    }
    this.saving = true;
    const call = this.editingId ? this.api.updateCapabilityModel(this.editingId, body) : this.api.createCapabilityModel(body);
    call.subscribe({
      next: (saved) => {
        this.saving = false;
        this.editorOpen = false;
        if (saved && saved.id) {
          this.models = applyDefault(this.models, saved);
          this.regroup();
        }
        this.load();
      },
      error: (err) => {
        this.saving = false;
        this.formError = err?.error?.message || 'Saving failed.';
      },
    });
  }

  makeDefault(m: CapabilityModel): void {
    if (m.isDefault) return;
    this.loadError = '';
    this.api.updateCapabilityModel(m.id, { isDefault: true, enabled: true }).subscribe({
      next: (saved) => {
        this.models = applyDefault(this.models, saved && saved.id ? saved : { ...m, isDefault: true, enabled: true });
        this.regroup();
      },
      error: (err) => (this.loadError = err?.error?.message || 'Making the model the default failed.'),
    });
  }

  toggleEnabled(m: CapabilityModel): void {
    if (m.isDefault && m.enabled) {
      this.loadError = 'The default model cannot be disabled. Make another model the default first.';
      return;
    }
    this.loadError = '';
    this.api.updateCapabilityModel(m.id, { enabled: !m.enabled }).subscribe({
      next: (saved) => {
        this.models = this.models.map((x) => (x.id === m.id ? (saved && saved.id ? saved : { ...m, enabled: !m.enabled }) : x));
        this.regroup();
      },
      error: (err) => (this.loadError = err?.error?.message || 'Saving failed.'),
    });
  }

  async remove(m: CapabilityModel): Promise<void> {
    const confirm = await deleteDialog();
    if (!confirm.isConfirmed) {
      return;
    }
    this.api.deleteCapabilityModel(m.id).subscribe({
      next: () => this.load(),
      error: (err) => (this.loadError = err?.error?.message || 'Deleting failed.'),
    });
  }

  private regroup(): void {
    this.groups = groupCapabilityModels(this.models, this.capabilityFilter);
  }

  private blankForm(capability: Capability): CapabilityForm {
    return {
      capability,
      providerId: '',
      modelId: '',
      displayName: '',
      enabled: true,
      isDefault: false,
      optionsText: formatOptions(starterOptions(capability)),
    };
  }
}
